import { authService } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/suvadi';

// API client helper
const apiClient = {
  async post<T>(url: string, data: FormData | any, options?: any): Promise<{ data: T }> {
    const token = authService.getToken();
    const headers: HeadersInit = {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(!(data instanceof FormData) && { 'Content-Type': 'application/json' }),
      ...options?.headers,
    };

    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers,
      body: data instanceof FormData ? data : JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    const result = await response.json();
    return { data: result };
  },

  async get<T>(url: string): Promise<{ data: T }> {
    const token = authService.getToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    const result = await response.json();
    return { data: result };
  },
};

export interface UserDocument {
  id: string;
  userId: string;
  originalFilename: string;
  storagePath: string;
  contentHash?: string;
  uploadTimestamp: string;
  fileType: string;
  fileSize: number;
  processingStatus: 'PENDING' | 'COMPLETED' | 'FAILED';
  ocrConfidence?: number;
  errorMessage?: string;
  processedAt?: string;
}

export interface ExtractionResult {
  success: boolean;
  documentId?: string;
  warrantyData?: WarrantyExtractionData;
  confidence?: number;
  message?: string;
  error?: string;
  ocrText?: string;
  storagePath?: string;
  duplicate?: boolean;
  duplicateInfo?: DuplicateInfo;
}

export interface DuplicateInfo {
  existingDocumentId: string;
  uploadedAt: string;
  existingWarranty?: WarrantyExtractionData;
  hasExistingWarranty: boolean;
  existingConfidence?: number;
}

export interface WarrantyExtractionData {
  productName?: string;
  brand?: string;
  modelNumber?: string;
  serialNumber?: string;
  purchaseDate?: string;
  warrantyPeriod?: string;
  warrantyExpiry?: string;
  retailer?: string;
  purchaseAmount?: number;
  category?: string;
  confidence?: number;
  extractedText?: string;
}

export interface Document {
  id: string;
  userId: string;
  originalFilename: string;
  fileType: string;
  fileSize: number;
  storagePath: string;
  processingStatus: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  confidenceScore?: number;
  errorMessage?: string;
  createdAt: string;
  processedAt?: string;
}

export const documentService = {
  /**
   * Extract warranty information from uploaded document
   */
  extractWarranty: async (file: File, userId: string): Promise<ExtractionResult> => {
    console.log('Preparing warranty extraction request:');
    console.log('- File:', file.name, 'Size:', file.size, 'Type:', file.type);
    console.log('- User ID:', userId);
    console.log('- API Base URL:', API_BASE_URL);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Use fetch directly to have more control over the request
      const token = authService.getToken();
      console.log('- Auth token present:', !!token);
      
      const url = `${API_BASE_URL}/api/v1/documents/extract-warranty?userId=${userId}`;
      console.log('- Full URL:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
          // Don't set Content-Type for FormData - let browser set it with boundary
        },
        body: formData,
      });

      console.log('- Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Extract warranty response:', response.status, response.statusText, errorText);
        throw new Error(`API request failed: ${response.statusText} (${response.status}) - ${errorText}`);
      }

      const result = await response.json();
      console.log('- API response:', result);
      return result;
    } catch (error) {
      console.error('Error extracting warranty:', error);
      throw error;
    }
  },

  /**
   * Get document processing history for a user
   */
  getDocumentHistory: async (userId: string): Promise<Document[]> => {
    try {
      const response = await apiClient.get<UserDocument[]>(`/api/v1/documents/user/${userId}/history`);
      
      // Convert UserDocument to Document interface for frontend compatibility
      const documents: Document[] = response.data.map((userDoc: UserDocument) => ({
        id: userDoc.id,
        userId: userDoc.userId,
        originalFilename: userDoc.originalFilename,
        fileType: userDoc.fileType,
        fileSize: userDoc.fileSize,
        storagePath: userDoc.storagePath,
        processingStatus: userDoc.processingStatus,
        confidenceScore: userDoc.ocrConfidence,
        createdAt: userDoc.uploadTimestamp,
        processedAt: userDoc.processedAt
      }));
      
      return documents;
    } catch (error) {
      console.error('Failed to load document history:', error);
      throw error;
    }
  },

  /**
   * Process image from camera or file upload for warranty extraction
   */
  processImage: async (file: File, userId: string): Promise<ExtractionResult> => {
    return documentService.extractWarranty(file, userId);
  },

  /**
   * Check document service health
   */
  healthCheck: async (): Promise<string> => {
    try {
      const response = await apiClient.get<string>('/api/v1/documents/health');
      return response.data;
    } catch (error) {
      console.error('Document service health check failed:', error);
      throw error;
    }
  },

  /**
   * Process OCR text with LLM for warranty extraction
   */
  processOcrWithLLM: async (ocrText: string, documentId: string): Promise<WarrantyExtractionData> => {
    console.log('🔄 Processing OCR text with LLM:', { documentId, ocrTextLength: ocrText.length });
    
    try {
      const user = await authService.getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const authToken = localStorage.getItem('authToken');
      const apiUrl = `${API_BASE_URL}/api/v1/documents/${documentId}/process-warranty-llm`;
      
      console.log('🌐 LLM API URL:', apiUrl);
      console.log('👤 Auth token present:', !!authToken);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ocrText,
          userId: user.userId || ''
        })
      });

      console.log('📡 LLM API Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ LLM API Error response:', errorText);
        throw new Error(`LLM processing failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ LLM API Success response:', result);
      return result;
      
    } catch (error) {
      console.error('💥 LLM processing error:', error);
      throw error;
    }
  },
};
